import React, { useMemo, useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import { ColDef } from "ag-grid-community";

import CygnusGrid from "../../reusable_components/CygnusGrid/CygnusGrid";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import { handleDownload } from "../../../Reusables/components/TemplateDownload/TemplateDownload.utils";
import COIRegistryModal from "./components/COIRegistryModal/COIRegistryModal";
import COIRegistryUploadModal from "./components/COIRegistryUploadModal/COIRegistryUploadModal";
import { fetchCOIRegistryEntries } from "./COIRegistry.service";
import { AffiliateTypeRenderer, YesNoRenderer, BtnCellRenderer } from "./COIRegistry.utils";
import { AFFILIATE_TYPE_CODES, COIRegistryEntry, COIRegistryProps } from "./COIRegistry.types";
import styles from "./COIRegistry.module.css";

const COIRegistry: React.FC<COIRegistryProps> = () => {
  const [rowData, setRowData] = useState<COIRegistryEntry[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<COIRegistryEntry>();
  const [modalShow, setModalShow] = useState(false);
  const [uploadModalShow, setUploadModalShow] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [gridKey, setGridKey] = useState<number>();
  const [downloading, setDownloading] = useState(false);

  const loadEntries = async () => {
    setRefreshGrid(true);
    try {
      const entries = await fetchCOIRegistryEntries();
      setGridKey(entries.length);
      setRowData(entries);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshGrid(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
       {
        headerName: "COI Registry Id",
        field: "COIRegistryId",
        flex: 1,
        minWidth: 100,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Client Id",
        field: "SECClientId",
        flex: 1,
        minWidth: 160,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Client Name",
        field: "SECClientName",
        flex: 1,
        minWidth: 160,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Primary Partner",
        field: "SECClientPartner",
        flex: 1,
        minWidth: 120,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Affiliate Type",
        field: "AffiliateTypeCode",
        flex: 1,
        minWidth: 130,
        cellRenderer: AffiliateTypeRenderer,
      },
      {
        headerName: "Affiliate",
        field: "AffiliateEntityName",
        flex: 1,
        minWidth: 160,
        autoHeight: true,
        wrapText: true,
        valueGetter: (params: any) =>
          params.data.AffiliateTypeCode === AFFILIATE_TYPE_CODES.ENTITY
            ? params.data.AffiliateEntityName
            : [params.data.AffiliateFirstName, params.data.AffiliateMiddleName, params.data.AffiliateLastName]
                .filter(Boolean)
                .join(" "),
      },
      {
        headerName: "Relationship To SEC Client",
        field: "RelationshipToSECClient",
        flex: 1,
        minWidth: 170,
        autoHeight: true,
        wrapText: true,
      },   
      {
        headerName: "Affiliate Current Client",
        field: "IsAffiliateACurrentClient",
        flex: 1,
        minWidth: 140,
        cellRenderer: YesNoRenderer,
      },
      {
        headerName: "COI Restriction",
        field: "COIRestriction",
        flex: 1,
        minWidth: 130,
        cellRenderer: YesNoRenderer,
      }, 
      {
        headerName: "Comments",
        field: "Comment",
        flex: 1,
        minWidth: 170,
        autoHeight: true,
        wrapText: true,
        tooltipValueGetter: (params: any) => params.value || "",
      },
      {
        headerName: "Created By",
        field: "CreatedBy",
        flex: 1,
        minWidth: 170,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Updated Date",
        field: "UpdatedDate",
        cellRenderer: TimeRenderer,
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: "Action",
        field: "",
        minWidth: 90,
        maxWidth: 90,
        filter: false,
        sortable: false,
        pinned: "right",
        cellRenderer: BtnCellRenderer(setSelectedRowData, setModalShow, loadEntries),
      },
    ],
    []
  );

  return (
    <>
      {modalShow && (
        <COIRegistryModal
          selectedRowData={selectedRowData}
          show={modalShow}
          onHide={(shouldRefresh) => {
            setModalShow(false);
            if (shouldRefresh) {
              loadEntries();
            }
          }}
        />
      )}

      {uploadModalShow && (
        <COIRegistryUploadModal
          show={uploadModalShow}
          onHide={(shouldRefresh) => {
            setUploadModalShow(false);
            if (shouldRefresh) {
              loadEntries();
            }
          }}
        />
      )}

      <Row className={`no-gutter ${styles.headerRow}`}>
        <Col md={6}>
          <p className={`display-6 ${styles.pageTitle}`}>COI Registry</p>
        </Col>
        <Col md={6} className={`d-flex justify-content-end align-items-center ${styles.actionRow}`}>
          <CustomButton
            progress={downloading}
            onclick={() =>
              handleDownload(
                "/firm_setup/coi_registry_export_excel",
                {},
                "COI_Registry",
                "xlsx",
                setDownloading
              )
            }
            content="Download"
          />
          <CustomButton
            onclick={() => setUploadModalShow(true)}
            content="Bulk Upload"
          />
          <div className={styles.reloadWrapper}>
            {refreshGrid ? (
              <ThreeCircles
                height="20"
                width="20"
                color="#4fa94d"
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
            ) : (
              <Icon.ArrowCounterclockwise onClick={() => loadEntries()} className={styles.reloadIcon} />
            )}
          </div>
        </Col>
      </Row>

      <Row style={{ height: "calc(95vh - 100px)" }}>
        <Col md={12} className={styles.registryGrid}>
          <CygnusGrid
            key={gridKey}
            fetchData={async () => ({
              records: rowData,
              totalCount: rowData.length,
            })}
            dataIdentifier="COIRegistryId"
            cachedData={rowData}
            setCachedData={setRowData}
            cachedIsLoading={refreshGrid}
            columnDefs={columnDefs}
            rowSelection={undefined}
            rowModelType="clientSide"
          />
        </Col>
      </Row>
    </>
  );
};

export default COIRegistry;
